import { Box, Button, Stack, Typography } from '@mui/material';
import { DisableContext, GeneralInformation, OperationBetweenRules, RuleItem, Visibility } from './components';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FC, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { schema } from './schema';
import { defaultValues, visibilityDefaultValue } from './defaultValues';
import { getCriteriasBodyForQuery, getLines, getVisibilityBody } from './Selections.helpers';
import { VisibilityTypes, profileApiSlice, selectionsApiSlice, setInitialScope } from '@src/api';
import { buildDataTestIdAttribute, buildFullDataTestId, DATA_TEST_ID_SELECTION, log } from '@src/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as LeftArrow } from '@icons/Arrows/left.svg';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { PortalContext } from '@src/context/portal';
import { VisibilityProvider } from './components/VisibilityProvider';
import { ROUTERS_PATH } from '@src/routers/consts';
import { useDispatch } from 'react-redux';
import { toast } from '@components/RootNotificationWrapper';

type RuleWithOperationProps = {
  rule?: any;
  index: number;
  arr?: { [key: string]: string }[];
  append: any;
  remove: any;
  insert: any;
  prefixDataTestId?: string;
};

const RuleWithOperation: FC<RuleWithOperationProps> = ({
  rule,
  index,
  arr,
  append,
  remove,
  insert,
  prefixDataTestId,
}) => {
  const disabled = useContext(DisableContext);
  const criteriaElement = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<any>(null);

  useEffect(() => {
    const { operationType, sx } = getLines(rule, disabled, index, arr, criteriaElement.current?.offsetHeight);
    setLines({ operationType, sx });
  }, [rule, arr, index, disabled]);

  return (
    <Stack position="relative" sx={lines?.sx}>
      <RuleItem
        ref={criteriaElement}
        rule={rule}
        prefix={`criterias.${index}`}
        append={append}
        insert={insert}
        remove={remove}
        operationType={lines?.operationType}
        withoutDelete={arr?.length === 1}
        isLast={arr?.length === index + 1}
        prefixDataTestId={prefixDataTestId}
      />
      <OperationBetweenRules prefix={`criterias.${index}.nextOperation`} ruleGroup={rule} />
    </Stack>
  );
};

export const Selection: FC = () => {
  const navigate = useNavigate();
  const { data: profile } = profileApiSlice.useGetProfileQuery('');
  const { module } = useContext(PortalContext);

  const dispatch = useDispatch();

  const { selectionId, copyId } = useParams();
  const [getSelection, { data: selection, isFetching, isSuccess }] = selectionsApiSlice.useLazyGetSelectionByIdQuery();
  const [copySelection, { isFetching: copyIsFetching }] = selectionsApiSlice.useLazyCopySelectionByIdQuery();

  const defaultValuesInner = useMemo(() => defaultValues(!!selectionId), [selectionId]);
  const [createSelection] = selectionsApiSlice.useCreateSelectionMutation();
  const [updateSelection] = selectionsApiSlice.useUpdateSelectionMutation();
  const [checkUniqueName] = selectionsApiSlice.useLazyValidateUniqueSelectionNameQuery();

  const dataTestId = buildFullDataTestId(DATA_TEST_ID_SELECTION);

  const {
    control,
    reset,
    formState: { errors, isValid, ...formStateRes },
    getValues,
    watch,
    ...methods
  } = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValuesInner },
    mode: 'onSubmit',
  });

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'criterias',
  });

  const [criterias] = watch(['criterias']);

  const disabled = selection?.isEditable === false;

  const controlledCriterias = fields.map((field, index) => {
    return {
      ...field,
      ...criterias?.[index],
    };
  });

  useWatch({ control });

  useLayoutEffect(() => {
    if (selectionId) {
      getSelection(selectionId)
        .then(res => {
          return res.data ? reset(res.data) : navigate(ROUTERS_PATH.SELECTIONS);
        })
        .catch(log);
    }
  }, [selectionId, navigate, getSelection, reset]);

  const isLabManager = module === 'LIS' && profile?.permissions.includes('selections.labmanager');

  useEffect(() => {
    if (copyId && profile) {
      copySelection(copyId)
        .then(
          res =>
            res.data &&
            reset({
              ...res.data,
              name: `${res.data.name} (Copy)`,
              visibilityType: VisibilityTypes.ForMe,
              createVisibility: isLabManager ? visibilityDefaultValue : null,
            }),
        )
        .catch(log);
    }
  }, [copyId, copySelection, reset, isLabManager, profile]);

  useEffect(() => {
    const contentContainer = document.querySelector('#content-container') as HTMLElement;

    if (contentContainer) {
      contentContainer.style.paddingTop = '0px';
    }

    return () => {
      if (contentContainer) {
        contentContainer.style.paddingTop = '';
      }
    };
  }, []);

  const saveSelection = () => {
    const {
      criterias: criteriasForm,
      visibilityType: visibilityTypeValue,
      createVisibility,
      name,
      ...rest
    } = getValues();

    methods
      .trigger()
      .then(async check => {
        if (!check) return null;

        if (selection?.name && selection.name === name.trim()) {
          return true;
        }

        const uniqueName = await checkUniqueName(name);

        return uniqueName.data;
      })
      .then((res: any) => {
        if (!res) return;

        if (res?.data === false) {
          methods.setError('name', {
            type: 'manual',
            message: 'This name is already in use',
          });
          return null;
        }

        const criteriasBody = getCriteriasBodyForQuery(criteriasForm);
        const visibilityBody = createVisibility ? getVisibilityBody(!!selectionId, createVisibility) : null;
        return (selectionId ? updateSelection : createSelection)({
          ...rest,
          name: name.trim(),
          visibilityType: visibilityTypeValue as VisibilityTypes,
          criterias: criteriasBody,
          [selectionId ? 'updateVisibility' : 'createVisibility']: visibilityBody,
        });
      })
      .then((res: any) => {
        if (res.data.status === 200) {
          navigate('/');
          toast({ variant: 'success', message: 'The Selection has been saved' });
        }
        dispatch(setInitialScope());

        return '';
      })
      .catch(log);
  };

  const goBack = () => {
    navigate('/');
    dispatch(setInitialScope());
  };

  const titleAvailableSelection = `${selectionId || copyId ? 'Edit' : 'Add'} selection`;

  return (
    <DisableContext.Provider value={disabled}>
      <Stack gap="24px" width="100%" flexGrow={1}>
        <Stack
          sx={{
            flexDirection: 'column',
            gap: '24px',
            position: 'relative',
            top: 0,
            background: '#EFF8FA',
            pt: '24px',
          }}
        >
          <Stack direction="row" gap="4px">
            <Button
              startIcon={<LeftArrow />}
              variant="text"
              onClick={goBack}
              sx={{
                '&:hover': {
                  'svg path': {
                    fill: '#4B5563',
                  },
                },
              }}
              {...buildDataTestIdAttribute(dataTestId, 'button-back')}
            >
              <Typography color="grey.700" variant="body16Medium" lineHeight="20px">
                Selections
              </Typography>
            </Button>
          </Stack>
          <Typography color="grey.700" variant="head32Bold" lineHeight="36px">
            {`${disabled ? 'Selection' : titleAvailableSelection}`}
          </Typography>
        </Stack>
        <FormProvider
          control={control}
          formState={{ errors, isValid, ...formStateRes }}
          getValues={getValues}
          reset={reset}
          watch={watch}
          {...methods}
        >
          <Stack direction="row" gap="24px" width="100%" position="relative" flexGrow={1}>
            {(isFetching || copyIsFetching) && <PreloaderBubbles />}
            <GeneralInformation />
            <Stack bgcolor="#FFFFFF" flexGrow={1} width="70%">
              {disabled ? null : isLabManager ? (
                <Visibility isFetching={isSuccess} visibilityTypeDefault={selection?.visibilityType} />
              ) : (
                <VisibilityProvider control={control} />
              )}

              <Box padding="36px">
                <Typography color="grey.700" variant="head24Medium" lineHeight="34px" component="p" mb="24px">
                  Selection criteria
                </Typography>
                {controlledCriterias?.map((rule: any, index: number, arr: any[]) => {
                  return (
                    <RuleWithOperation
                      {...{ rule, index, arr }}
                      key={`${rule?.id}`}
                      append={append}
                      remove={remove}
                      insert={insert}
                      prefixDataTestId={dataTestId}
                    />
                  );
                })}
              </Box>
            </Stack>
          </Stack>
        </FormProvider>
        {!disabled && (
          <Stack padding="36px" bgcolor="#FFFFFF">
            <Stack direction="row" gap="12px" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                onClick={goBack}
                {...buildDataTestIdAttribute(dataTestId, 'button-cancel')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={saveSelection}
                {...buildDataTestIdAttribute(dataTestId, 'button-save')}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </DisableContext.Provider>
  );
};
